import { useEffect, useState } from "react";
import { collection, onSnapshot, query, type QueryConstraint } from "firebase/firestore";
import type { Appointment, Chair, Practitioner, Service } from "@cop/domain";
import { db, TENANT } from "./firebase";

export function useLive<T>(path: string, constraints: QueryConstraint[], deps: unknown[] = []) {
  const [data, setData] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    const q = query(collection(db, "tenants", TENANT, path), ...constraints);
    return onSnapshot(
      q,
      (s) => setData(s.docs.map((d) => ({ id: d.id, ...d.data() }) as T)),
      (e) => setError(e.message),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  return { data, error };
}

export function useCatalog() {
  const practitioners = useLive<Practitioner>("practitioners", []);
  const services = useLive<Service>("services", []);
  const chairs = useLive<Chair>("chairs", []);
  return {
    practitioners: (practitioners.data ?? []).filter((p) => p.active).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    services: (services.data ?? []).filter((s) => s.active).sort((a, b) => a.name.localeCompare(b.name)),
    chairs: chairs.data ?? [],
    ready: practitioners.data !== null && services.data !== null,
  };
}

export type { Appointment };
